// Regras puras da encomenda — espelham core/migrations/020_encomendas.sql.
// Nada de Supabase aqui: situação, prazo e ordenação são determinísticos e
// testáveis.
//
// POR QUE "EXPIRADA" MORA AQUI E NÃO NO BANCO (regra 4 do CLAUDE.md): expirada
// é `status = 'ativa'` com `expira_em` no passado. Gravar a palavra numa coluna
// exigiria um cron para virá-la à meia-noite, e esse cron erraria na virada do
// dia por fuso — a encomenda apareceria vencida à 21h de Rio Grande. Derivar da
// data é sempre certo e não depende de nada rodar.

import { dataCurtaDeISO } from './orcamento';

export type SituacaoEncomenda = 'ativa' | 'expirada' | 'atendida' | 'cancelada';

export interface EncomendaSituavel {
  status: 'ativa' | 'atendida' | 'cancelada';
  expira_em: string; // 'YYYY-MM-DD'
}

/**
 * Mesmo corte de reservado_da_peca(): a encomenda vale ATÉ o dia do prazo,
 * inclusive (`expira_em >= current_date`). Comparação de strings
 * 'YYYY-MM-DD' é comparação de datas, sem objeto Date e sem fuso.
 */
export function situacaoEncomenda(e: EncomendaSituavel, hojeISO: string): SituacaoEncomenda {
  if (e.status !== 'ativa') return e.status;
  return e.expira_em < hojeISO ? 'expirada' : 'ativa';
}

/** Dias que faltam para o prazo. Negativo = venceu há N dias. */
export function diasAteOPrazo(expiraEm: string, hojeISO: string): number {
  const dia = (iso: string) => {
    const [a, m, d] = iso.split('-').map(Number);
    return Date.UTC(a ?? 1970, (m ?? 1) - 1, d ?? 1);
  };
  return Math.round((dia(expiraEm) - dia(hojeISO)) / 86_400_000);
}

/**
 * O aviso do prazo em português de balcão. "Vence hoje" é caso próprio de
 * propósito: é o único dia em que o vendedor ainda pode ligar para o cliente
 * antes de a peça voltar para a prateleira.
 */
export function textoDoPrazo(e: EncomendaSituavel, hojeISO: string): string {
  const s = situacaoEncomenda(e, hojeISO);
  if (s === 'atendida') return 'entregue ao cliente';
  if (s === 'cancelada') return 'cancelada';

  const dias = diasAteOPrazo(e.expira_em, hojeISO);
  if (dias < 0) return `venceu ${dataCurtaDeISO(e.expira_em)}`;
  if (dias === 0) return 'vence hoje';
  if (dias === 1) return 'vence amanhã';
  return `vence em ${dias} dias (${dataCurtaDeISO(e.expira_em)})`;
}

/**
 * Prazo padrão de uma encomenda nova: 7 dias, o mesmo do orçamento — o cliente
 * que "volta sexta" e o que "pensa até sexta" são a mesma semana, e dois prazos
 * diferentes só dariam ao balcão mais uma coisa para decidir.
 */
export const PRAZO_PADRAO_DIAS = 7;

/**
 * Ordem da lista: o que exige AÇÃO primeiro. Expiradas no topo (a peça está
 * presa numa promessa morta), depois as ativas por prazo mais curto, e o que
 * já terminou no fim. Sem isto, a tela ordena por data e enterra justamente a
 * linha que alguém precisa resolver hoje.
 */
export function pesoNaLista(e: EncomendaSituavel, hojeISO: string): number {
  const s = situacaoEncomenda(e, hojeISO);
  if (s === 'expirada') return 0;
  if (s === 'ativa') return 1;
  return 2;
}

// --- AS PEÇAS DO PEDIDO (066) ------------------------------------------------
// A encomenda virou lista. As duas regras abaixo espelham 1:1 o que o banco
// faz — total_da_encomenda() e o trigger do nome da peça diversa — para o
// balcão descobrir na tela, e não no erro.

export interface PecaEncomendada {
  /** Nulo = peça excluída do catálogo. O histórico não some com o cadastro. */
  peca: { descricao: string } | null;
  /** PEÇA DIVERSA (9999): o nome escrito à mão. */
  descricao_avulsa: string | null;
}

/**
 * O nome da peça na ordem em que a verdade mora — a mesma régua de
 * nomeDoItemVendido() (coringa.ts), uma etapa antes da venda:
 *
 *  1. o nome escrito na peça diversa: nela o produto é o coringa 9999, cuja
 *     descrição ("PEÇA DIVERSA — BALCÃO") não diz o que o cliente pediu;
 *  2. a descrição do produto cadastrado;
 *  3. o aviso de peça removida do catálogo — a promessa ao cliente não some
 *     junto com o cadastro, e a linha precisa dizer isso em vez de ficar vazia.
 */
export function nomeDaPecaEncomendada(i: PecaEncomendada): string {
  const avulsa = (i.descricao_avulsa ?? '').trim();
  if (avulsa) return avulsa;
  return i.peca?.descricao ?? '(peça removida do catálogo)';
}

/**
 * O combinado da encomenda: a soma das peças (066). Null enquanto UMA linha
 * estiver sem valor — "a combinar" —, que é exatamente o que
 * `total_da_encomenda()` devolve no banco.
 *
 * Somar só as linhas já precificadas daria um número que parece fechado e não
 * é; e é ele que decide se a peça sai da loja.
 */
export function totalDaEncomenda(itens: Array<{ valor: number | null }>): number | null {
  if (itens.length === 0) return null;
  if (itens.some((i) => i.valor === null)) return null;
  return itens.reduce((soma, i) => soma + Number(i.valor), 0);
}

/**
 * O COMBINADO DE UMA LINHA: preço da unidade × quantidade, fechado em centavos.
 * É a mesma conta do carrinho da venda (`quantidade * preco_unit`, 041), e era
 * ela que faltava aqui.
 *
 * O BUG QUE ISTO CORRIGE (01/09/2026): a tela guardava o valor da linha JÁ
 * somado e só o recalculava a partir do preço de tabela. A peça diversa (9999)
 * não tem tabela — então três unidades de R$ 50 seguiam valendo R$ 50 no papel
 * do cliente. O mesmo acontecia, calado, na peça cadastrada cujo preço fosse
 * digitado à mão. Agora quem se digita é o preço DA UNIDADE, e a linha é a
 * multiplicação — que é como o balcão fala ("três, cinquenta cada").
 *
 * Preço nulo continua nulo depois da conta: "a acertar" não é zero, e zero é um
 * preço de verdade (peça de brinde).
 */
export function combinadoDaLinha(
  precoUnit: number | null,
  quantidade: number | null,
): number | null {
  if (precoUnit === null || quantidade === null || quantidade <= 0) return null;
  return Math.round(precoUnit * quantidade * 100) / 100;
}

/**
 * O caminho de volta, para ABRIR uma linha que já existe: o banco guarda o
 * combinado da linha (`reservation_items.valor`), e o campo da tela mostra o
 * preço da unidade.
 *
 * A divisão pode não fechar em centavos ("os três por 100" = 33,33 cada), e é
 * por isso que a tela só reescreve o valor da linha quando alguém mexe nela —
 * abrir e fechar uma encomenda antiga não pode mudar o que o cliente combinou.
 */
export function precoUnitDaLinha(
  valor: number | null,
  quantidade: number,
): number | null {
  if (valor === null || !(quantidade > 0)) return null;
  return Math.round((valor / quantidade) * 100) / 100;
}

/**
 * A conta da encomenda fechou? Meio centavo de tolerância: arredondamento de
 * parcela não pode reabrir uma conta que o cliente já quitou.
 */
export function contaFechada(pago: number, combinado: number | null): boolean {
  return combinado !== null && combinado > 0 && combinado - pago < 0.005;
}

/**
 * O QUE CONFERIR ANTES DE "CLIENTE LEVOU" (25/08/2026): dar baixa não registra
 * venda. Numa encomenda com dinheiro recebido e nenhuma venda, a baixa deixaria
 * entrada no caixa sem venda no histórico — e o estoque sem baixa. É AVISO, não
 * trava (decisão do dono): a tela pergunta, quem atende decide.
 *
 *   'fechar-venda' → conta fechada e venda nenhuma;
 *   'conta-aberta' → tem pagamento, mas falta dinheiro ou falta o combinado;
 *   null           → segue direto (sem dinheiro pendente, ou a venda já existe).
 *
 * A 068 (01/09/2026) não aposentou este aviso, mudou quem ele pega. A conta
 * que fecha registra a venda sozinha, então 'fechar-venda' deixou de ser o
 * passo esquecido do fluxo e passou a ser a exceção: encomenda quitada antes
 * da mudança, ou conversão que o banco recusou (teto de preço, peça no
 * carrinho do colega). 'conta-aberta' não mudou — conta aberta nunca virou
 * venda, nem antes nem agora.
 */
export type AvisoAoLevar = 'fechar-venda' | 'conta-aberta' | null;

export function avisoAoLevar(p: {
  pago: number; combinado: number | null; virouVenda: boolean;
}): AvisoAoLevar {
  if (p.virouVenda || p.pago <= 0) return null;
  return contaFechada(p.pago, p.combinado) ? 'fechar-venda' : 'conta-aberta';
}
